/**
 * contexts/DirtyContext.tsx
 * ------------------------------------------------------------------
 * Tiny global "unsaved work" registry for the version gate (slice 2,
 * layer C). Forms that hold unsaved edits register a key while dirty and
 * clear it when saved; the version-gate auto-reload consults
 * hasUnsavedWork() so it NEVER reloads over unsaved work.
 *
 * We do NOT invent per-form dirty state - MarkEntry and CommentReview
 * already own a local `dirty` boolean. useDirtyFlag() simply mirrors that
 * existing flag into this registry (and clears it on unmount).
 * ------------------------------------------------------------------
 */
import {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';

interface DirtyContextValue {
  registerDirty: (key: string) => void;
  clearDirty: (key: string) => void;
  /** True if any registered form currently holds unsaved work. */
  hasUnsavedWork: () => boolean;
  /** Reactive count (for consumers that want to re-render on change). */
  dirtyCount: number;
}

const DirtyContext = createContext<DirtyContextValue | undefined>(undefined);

export function DirtyProvider({ children }: { children: ReactNode }) {
  const keys = useRef<Set<string>>(new Set());
  const [dirtyCount, setDirtyCount] = useState(0);

  const registerDirty = useCallback((key: string) => {
    if (keys.current.has(key)) return;
    keys.current.add(key);
    setDirtyCount(keys.current.size);
  }, []);
  const clearDirty = useCallback((key: string) => {
    if (!keys.current.has(key)) return;
    keys.current.delete(key);
    setDirtyCount(keys.current.size);
  }, []);
  // Reads the live ref, so it is always current at the moment auto-reload
  // checks it (not tied to a render snapshot).
  const hasUnsavedWork = useCallback(() => keys.current.size > 0, []);

  return (
    <DirtyContext.Provider value={{ registerDirty, clearDirty, hasUnsavedWork, dirtyCount }}>
      {children}
    </DirtyContext.Provider>
  );
}

export function useDirty(): DirtyContextValue {
  const ctx = useContext(DirtyContext);
  if (!ctx) throw new Error('useDirty must be used within <DirtyProvider>');
  return ctx;
}

/**
 * Mirror an existing local `dirty` boolean into the registry under a
 * stable key. Clears the key on unmount so a navigated-away form never
 * leaves a stale "dirty" marker that would block auto-reload forever.
 */
export function useDirtyFlag(key: string, dirty: boolean): void {
  const { registerDirty, clearDirty } = useDirty();
  useEffect(() => {
    if (dirty) registerDirty(key);
    else clearDirty(key);
    return () => clearDirty(key);
  }, [key, dirty, registerDirty, clearDirty]);
}
